/**
 * SSR-safe head builder for TanStack route `head()` options.
 *
 * Page components must NOT use react-helmet-async for indexable metadata —
 * Helmet only runs in the browser, so crawlers saw the generic root tags.
 * Routes call `buildHead(...)` inside `head()` instead, which renders into
 * the server HTML via <HeadContent />.
 */

export const SITE_URL = "https://lohalink.in";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export interface SeoInput {
  title: string;
  description: string;
  /** Absolute canonical URL, or a site-relative path starting with "/". */
  canonical: string;
  ogType?: "website" | "article";
  /** Absolute https image URL. Falls back to the site OG image. */
  image?: string;
  noindex?: boolean;
  /** JSON-LD graph objects injected as application/ld+json scripts. */
  jsonLd?: Record<string, unknown>[];
  /** Extra meta entries passed straight through. */
  extraMeta?: Record<string, string>[];
}

export interface HeadResult {
  meta: Record<string, string>[];
  links: Record<string, string>[];
  scripts: { type: string; children: string }[];
}

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function buildHead({
  title,
  description,
  canonical,
  ogType = "website",
  image,
  noindex,
  jsonLd,
  extraMeta,
}: SeoInput): HeadResult {
  const url = absoluteUrl(canonical);
  const img = image ?? DEFAULT_OG_IMAGE;

  const meta: Record<string, string>[] = [
    { title },
    { name: "description", content: description },
    {
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    },
    { property: "og:type", content: ogType },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: img },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: img },
    ...(extraMeta ?? []),
  ];

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
    scripts: (jsonLd ?? []).map((graph) => ({
      type: "application/ld+json",
      children: JSON.stringify(graph),
    })),
  };
}
